import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import axios from "axios";
import { registerAuthInterceptor, setLoggingOut } from "../lib/authInterceptor";
import { dropPushSubscription } from "../lib/pushLifecycle";
import {
  Building,
  ServiceProvider,
  EntityType,
  AuthResponse,
  User,
  isBuildingAuth,
  isServiceProviderAuth,
  isUserAuth,
} from "../types";

interface AuthContextType {
  // User-based auth (email/password or Google)
  user: User | null;

  // Entity-based auth (admin impersonation)
  entityType: EntityType | null;
  entityId: string | null;
  entityName: string | null;
  building: Building | null;
  serviceProvider: ServiceProvider | null;

  // True when the underlying authenticated user has admin rights, whether
  // they are currently impersonating an entity or not.
  isAdmin: boolean;

  // Whether this deployment has the assistant (ticket 21, §D15). Read from the
  // /auth/me payload rather than a probe, and false until it has answered — the
  // widget must not flash into existence on a deployment without the feature.
  assistantEnabled: boolean;

  // Whether this deployment has the translate buttons (ticket 23, §D10). Its
  // own field on the same channel: the two features are gated separately, and
  // false means no button is rendered at all.
  translationEnabled: boolean;

  // Whether this deployment reads uploaded documents (ticket 24). Again its own
  // field: false means no «Распознать» button and no auto-run at all — zero
  // trace, as the other three have.
  recognitionEnabled: boolean;

  // State
  isLoading: boolean;
  isAuthenticated: boolean;

  // Actions
  loginAsEntity: (entityType: EntityType, entityId: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
}

/**
 * Exported since ticket 23 so `useTranslationEnabled` can read it *without*
 * throwing when there is no provider. `useAuth` still throws, which is right
 * for a screen that cannot work signed-out; a translate button is an
 * affordance, and "no cabinet, no button" is the correct reading of its
 * absence.
 */
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_URL = process.env.REACT_APP_API_URL || "";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [entityType, setEntityType] = useState<EntityType | null>(null);
  const [entityId, setEntityId] = useState<string | null>(null);
  const [entityName, setEntityName] = useState<string | null>(null);
  const [building, setBuilding] = useState<Building | null>(null);
  const [serviceProvider, setServiceProvider] =
    useState<ServiceProvider | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [assistantEnabled, setAssistantEnabled] = useState(false);
  const [translationEnabled, setTranslationEnabled] = useState(false);
  const [recognitionEnabled, setRecognitionEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const clearAuth = () => {
    setUser(null);
    setEntityType(null);
    setEntityId(null);
    setEntityName(null);
    setBuilding(null);
    setServiceProvider(null);
    setIsAdmin(false);
    setAssistantEnabled(false);
    setTranslationEnabled(false);
    setRecognitionEnabled(false);
  };

  const fetchAuth = async () => {
    try {
      const response = await axios.get<AuthResponse>(`${API_URL}/auth/me`, {
        withCredentials: true,
      });

      const data = response.data;
      setAssistantEnabled(
        (data as { assistantEnabled?: boolean }).assistantEnabled === true
      );
      setTranslationEnabled(
        (data as { translationEnabled?: boolean }).translationEnabled === true
      );
      setRecognitionEnabled(
        (data as { recognitionEnabled?: boolean }).recognitionEnabled === true
      );

      if (isBuildingAuth(data)) {
        setUser(null);
        setEntityType("building");
        setEntityId(data.entityId);
        setEntityName(data.entityName);
        setBuilding(data.building);
        setServiceProvider(null);
        setIsAdmin(data.isAdmin === true);
      } else if (isServiceProviderAuth(data)) {
        setUser(null);
        setEntityType("service_provider");
        setEntityId(data.entityId);
        setEntityName(data.entityName);
        setServiceProvider(data.serviceProvider);
        setBuilding(null);
        setIsAdmin(data.isAdmin === true);
      } else if (isUserAuth(data)) {
        // Traditional user auth (email/password or Google)
        setUser(data);
        setEntityType(null);
        setEntityId(null);
        setEntityName(null);
        setBuilding(null);
        setServiceProvider(null);
        setIsAdmin(data.isAdmin === true);
      } else {
        clearAuth();
      }
    } catch (error) {
      clearAuth();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAuth();
  }, []);

  // Global session-death handling (ticket 7): a destroyed session's next 401
  // routes to the login page instead of leaving a zombie UI. Registered for
  // the whole app lifetime, so /select-role and onboarding are covered too.
  useEffect(() => {
    return registerAuthInterceptor(() => {
      // Browser side only: the /unsubscribe call would 401 for the same reason
      // we are here. The orphaned row reaps itself on the next 410 from the
      // push service (ticket 12, spec §E).
      void dropPushSubscription({ notifyServer: false });
      clearAuth();
      window.location.href = "/login?error=session_expired";
    });
  }, []);

  const loginAsEntity = async (type: EntityType, id: string) => {
    try {
      setIsLoading(true);
      const response = await axios.post(
        `${API_URL}/api/admin/login`,
        { entityType: type, entityId: id },
        { withCredentials: true }
      );

      if (response.data.success) {
        // Refresh auth state
        await fetchAuth();
      }
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    // From here on a 401 is this logout's own doing, not an expired session:
    // the signed-in frame stays mounted until the navigation below, and whatever
    // it still has in flight must not be answered with ?error=session_expired.
    setLoggingOut(true);
    try {
      // Before the POST, and awaited: the subscription belongs to the principal
      // (ticket 12, spec §E). It has to happen here rather than in a
      // PushProvider effect, because this function ends in a full navigation
      // that would race one. Time-boxed and non-throwing, so logout is never
      // blocked by it.
      await dropPushSubscription({ notifyServer: true });

      await axios.post(
        `${API_URL}/auth/logout`,
        {},
        {
          withCredentials: true,
        }
      );
      // A logout ends in a full navigation on purpose — nothing in memory
      // outlives the session. What it must NOT do first is clear the auth state:
      // that paints the cabinet as signed-out, lets ProtectedRoute redirect to
      // /login, and only then reloads — three paints, which is the flicker QA
      // reported (BUG-42). `replace` drops the page we are leaving from history,
      // so the first Back does not land on the screen we just signed out of.
      window.location.replace("/login");
    } catch (error) {
      console.error("Logout error:", error);
      // The session is still alive and so is the page: 401s mean what they
      // usually mean again.
      setLoggingOut(false);
    }
  };

  const refreshAuth = async () => {
    await fetchAuth();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        entityType,
        entityId,
        entityName,
        building,
        serviceProvider,
        isAdmin,
        assistantEnabled,
        translationEnabled,
        recognitionEnabled,
        isLoading,
        isAuthenticated: !!user || (!!entityType && !!entityId),
        loginAsEntity,
        logout,
        refreshAuth,
      }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
